import { observer } from 'mobx-react-lite';
import { NavLink } from 'react-router-dom';
import { t } from '../../services/LocalizationService';
import {
  BarChart2,
  ClipboardList,
  Cpu,
  Monitor,
  ScrollText,
  Settings as SettingsIcon,
  TrendingUp,
  Users,
  type LucideIcon,
} from 'lucide-react';
import emfitCareLogo from '../../assets/emfit_care.png';
import { Sidebar } from '@emfit/shared-ui';
import { SessionStore } from '../../stores';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

type Item = { path: string; label: string; icon: LucideIcon };

const linkClass = ({ isActive }: { isActive: boolean }) =>
  [
    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
    isActive
      ? 'bg-primary-50 text-primary-700 font-semibold dark:bg-primary-900/20 dark:text-primary-300'
      : 'text-foreground-secondary hover:bg-gray-100 hover:text-foreground dark:hover:bg-gray-700',
  ].join(' ');

function Section({
  title,
  items,
  onNavigate,
}: {
  title?: string;
  items: Item[];
  onNavigate: () => void;
}) {
  if (!items.length) return null;
  const id = title ? `nav-${title.replace(/\W+/g, '-').toLowerCase()}` : undefined;
  return (
    <div>
      {title && (
        <h2
          id={id}
          className="px-3 mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        >
          {title}
        </h2>
      )}
      <ul className="list-none space-y-0.5" aria-labelledby={id}>
        {items.map((item) => (
          <li key={item.path}>
            <NavLink to={item.path} onClick={onNavigate} className={linkClass}>
              <item.icon className="w-5 h-5 shrink-0" aria-hidden="true" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Grouped by what a shift does: what is happening now first, then looking
 * back, then looking after devices and people. The monitor leads because it
 * is the screen a night shift keeps open.
 */
export const NavigationSidebar = observer(
  ({ isOpen, onClose }: SidebarProps) => {
    const logo = <img src={emfitCareLogo} alt="Emfit Care" className="h-12" />;

    const now: Item[] = [
      { path: '/monitor', label: t('Monitor'), icon: Monitor },
      { path: '/trends', label: t('Timelines'), icon: TrendingUp },
    ];
    const review: Item[] = [
      { path: '/dashboard', label: t('Group dashboard'), icon: BarChart2 },
      { path: '/events', label: t('Events'), icon: ClipboardList },
    ];
    const manage: Item[] = [
      { path: '/devices', label: t('Devices'), icon: Cpu },
      ...(SessionStore.isAdmin
        ? [{ path: '/users', label: t('Users & Groups'), icon: Users }]
        : []),
      ...(SessionStore.isMasterUser
        ? [{ path: '/audit-log', label: t('Audit Log'), icon: ScrollText }]
        : []),
    ];

    return (
      <Sidebar isOpen={isOpen} onClose={onClose} logo={logo} logoHref="/monitor">
        <nav className="flex flex-col gap-5" aria-label={t('Main')}>
          <Section title={t('Now')} items={now} onNavigate={onClose} />
          <Section title={t('Look back')} items={review} onNavigate={onClose} />
          <Section title={t('Manage')} items={manage} onNavigate={onClose} />
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <Section
              items={[{ path: '/settings', label: t('Profile'), icon: SettingsIcon }]}
              onNavigate={onClose}
            />
          </div>
        </nav>
      </Sidebar>
    );
  }
);

NavigationSidebar.displayName = 'NavigationSidebar';
